import React, { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import { DadosRenovacao, ProdutoRenovacao, ProdutoRenovacaoInput, RenovarContratoParams } from '../../../types/contratos';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale/pt-BR';
import { toast } from "react-toastify";
import Select from "react-select";

// Configuração do DatePicker (mesma do contracts-list)
const MONTHS = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MONTHS_SHORT = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const customPtBR = {
    ...ptBR,
    localize: {
        ...ptBR.localize,
        month: (n: number) => MONTHS[n],
        monthShort: (n: number) => MONTHS_SHORT[n]
    }
};

registerLocale('pt-BR', customPtBR as any);

interface RenovacaoContratoModalProps {
    isOpen: boolean;
    onClose: () => void;
    contractId: number | null;
    onSuccess: () => void;
}

const RenovacaoContratoModal: React.FC<RenovacaoContratoModalProps> = ({ isOpen, onClose, contractId, onSuccess }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [dadosRenovacao, setDadosRenovacao] = useState<DadosRenovacao | null>(null);

    // Estados do formulário
    const [dataInicio, setDataInicio] = useState<Date | null>(null);
    const [dataFim, setDataFim] = useState<Date | null>(null);
    const [numeroContrato, setNumeroContrato] = useState<string>('');
    const [descricao, setDescricao] = useState<string>('');
    const [observacoes, setObservacoes] = useState<string>('');
    const [produtos, setProdutos] = useState<ProdutoRenovacao[]>([]);
    const [valorTotalCalculado, setValorTotalCalculado] = useState<number>(0);

    // Efeito para carregar dados quando o modal abre
    useEffect(() => {
        if (isOpen && contractId) {
            carregarDadosRenovacao(contractId);
        } else {
            // Resetar estados ao fechar
            setDadosRenovacao(null);
            setDataInicio(null);
            setDataFim(null);
            setNumeroContrato('');
            setDescricao('');
            setObservacoes('');
            setProdutos([]);
            setValorTotalCalculado(0);
        }
    }, [isOpen, contractId]);

    // Recalcular total sempre que os produtos mudarem
    useEffect(() => {
        const total = produtos.reduce((acc, produto) => {
            return acc + (produto.quantidade * produto.valor_proposta);
        }, 0);
        setValorTotalCalculado(total);
    }, [produtos]);

    const carregarDadosRenovacao = async (id: number) => {
        try {
            setIsLoading(true);
            const response = await apiService.getDadosRenovacao(id);
            
            if (response.success && response.data) {
                const dados = response.data;
                setDadosRenovacao(dados);
                
                // Preencher campos com sugestões
                // Data Início: Dia seguinte ao término atual
                const fimAtual = new Date(dados.data_finalizacao);
                const inicioSugerido = new Date(fimAtual);
                inicioSugerido.setDate(inicioSugerido.getDate() + 1);
                setDataInicio(inicioSugerido);

                // Data Fim: 1 ano após início sugerido (menos 1 dia)
                const fimSugerido = new Date(inicioSugerido);
                fimSugerido.setFullYear(fimSugerido.getFullYear() + 1);
                fimSugerido.setDate(fimSugerido.getDate() - 1);
                setDataFim(fimSugerido);

                setNumeroContrato(dados.numero_contrato); // Mantém o mesmo número
                setDescricao(dados.descricao || '');
                setProdutos(dados.produtos);
            } else {
                toast.error("Erro ao carregar dados para renovação.");
                onClose();
            }
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            toast.error("Erro ao carregar dados do contrato.");
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const handleProdutoChange = (index: number, novoValor: number) => {
        const novosProdutos = [...produtos];
        novosProdutos[index] = {
            ...novosProdutos[index],
            valor_proposta: novoValor
        };
        setProdutos(novosProdutos);
    };

    // Função helper para formatar data local sem conversão UTC
    const formatDateLocal = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!dadosRenovacao || !dataInicio || !dataFim || !contractId) {
            toast.error("Preencha todos os campos obrigatórios.");
            return;
        }

        if (valorTotalCalculado <= 0) {
            toast.error("O valor total do contrato deve ser maior que zero.");
            return;
        }

        try {
            setIsSubmitting(true);

            // Preparar dados para envio
            const valoresProdutos: ProdutoRenovacaoInput[] = produtos.map(p => ({
                id_produto_oportunidade: p.id, // O ID no array de produtos de renovação é o ID do produto_oportunidade
                quantidade: p.quantidade,
                valor_proposta: p.valor_proposta
            }));

            const params: RenovarContratoParams = {
                id_contrato: contractId,
                id_oportunidade: dadosRenovacao.id_oportunidade,
                data_inicio: formatDateLocal(dataInicio),
                data_fim: formatDateLocal(dataFim),
                numero_contrato: numeroContrato,
                descricao: descricao,
                observacoes: observacoes,
                valores_produtos: valoresProdutos
            };

            // Console.log para depuração
            console.log('=== DADOS DO FORMULÁRIO DE RENOVAÇÃO ===');
            console.log('Data Início (original):', dataInicio);
            console.log('Data Início (ISO):', dataInicio.toISOString());
            console.log('Data Início (formatada):', dataInicio.toISOString().split('T')[0]);
            console.log('Data Fim (original):', dataFim);
            console.log('Data Fim (ISO):', dataFim.toISOString());
            console.log('Data Fim (formatada):', dataFim.toISOString().split('T')[0]);
            console.log('Parâmetros completos:', params);
            console.log('=========================================');

            const response = await apiService.renovarContrato(params);

            if (response.success) {
                // Tratar a nova resposta com dois contratos
                const { contrato_antigo, contrato_novo, id_novo_contrato } = response.data;

                // Usar descrição ou número do contrato para mensagem mais amigável
                const infoContrato = contrato_novo.descricao || contrato_novo.oportunidade_titulo || `#${id_novo_contrato}`;
                toast.success(`Contrato renovado com sucesso! Novo contrato: ${infoContrato}`);
                onSuccess();
                onClose();
            } else {
                toast.error(response.message || "Erro ao renovar contrato.");
            }
        } catch (error: any) {
            console.error("Erro na renovação:", error);
            toast.error(error.message || "Erro ao realizar a renovação.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Formatação de moeda
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    return (
        <div
            className={`offcanvas offcanvas-end offcanvas-large ${isOpen ? 'show' : ''}`}
            tabIndex={-1}
            id="offcanvas_renovacao"
            style={{ visibility: isOpen ? 'visible' : 'hidden', width: '80%' }}
            aria-modal={isOpen}
            role="dialog"
        >
            <div className="offcanvas-header border-bottom">
                <h4>Renovar Contrato</h4>
                <button
                    type="button"
                    className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <i className="ti ti-x" />
                </button>
            </div>
            <div className="offcanvas-body">
                {isLoading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="mt-2">Carregando dados para renovação...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            {/* Dados do Contrato */}
                            <div className="col-md-12 mb-3">
                                <h5 className="mb-3">Dados da Renovação</h5>
                            </div>

                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="col-form-label">
                                        Data de Início <span className="text-danger">*</span>
                                    </label>
                                    <div className="icon-form">
                                        <span className="form-icon">
                                            <i className="ti ti-calendar-check" />
                                        </span>
                                        <DatePicker
                                            className="form-control datetimepicker"
                                            selected={dataInicio}
                                            onChange={(date) => setDataInicio(date)}
                                            dateFormat="dd/MM/yyyy"
                                            locale="pt-BR"
                                            placeholderText="Selecione a data"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="col-form-label">
                                        Data de Término <span className="text-danger">*</span>
                                    </label>
                                    <div className="icon-form">
                                        <span className="form-icon">
                                            <i className="ti ti-calendar-check" />
                                        </span>
                                        <DatePicker
                                            className="form-control datetimepicker"
                                            selected={dataFim}
                                            onChange={(date) => setDataFim(date)}
                                            dateFormat="dd/MM/yyyy"
                                            locale="pt-BR"
                                            placeholderText="Selecione a data"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label className="col-form-label">
                                        Número do Novo Contrato <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={numeroContrato}
                                        onChange={(e) => setNumeroContrato(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label className="col-form-label">
                                        Descrição
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={descricao}
                                        onChange={(e) => setDescricao(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-md-12">
                                <div className="mb-3">
                                    <label className="col-form-label">
                                        Observações
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={observacoes}
                                        onChange={(e) => setObservacoes(e.target.value)}
                                    ></textarea>
                                </div>
                            </div>

                            {/* Produtos */}
                            <div className="col-md-12 mt-4 mb-3">
                                <h5 className="mb-3">Produtos da Oportunidade</h5>
                                <div>
                                    <table className="table table-bordered table-striped" style={{ tableLayout: 'fixed', width: '100%' }}>
                                        <thead className="thead-light">
                                            <tr>
                                                <th style={{ width: '45%' }}>Produto</th>
                                                <th style={{ width: '10%' }}>Qtd.</th>
                                                <th style={{ width: '25%' }}>Novo Valor Unit.</th>
                                                <th style={{ width: '20%' }}>Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {produtos.map((produto, index) => (
                                                <tr key={produto.id}>
                                                    <td className="align-middle">
                                                        <div className="fw-medium text-truncate" title={produto.produto_nome}>{produto.produto_nome}</div>
                                                        <div className="small text-muted text-truncate" title={produto.descricao_especifica}>{produto.descricao_especifica}</div>
                                                    </td>
                                                    <td className="text-center align-middle">{parseFloat(String(produto.quantidade)).toString()}</td>
                                                    <td className="align-middle">
                                                        <div className="input-group input-group-sm">
                                                            <span className="input-group-text">R$</span>
                                                            <input
                                                                type="number"
                                                                className="form-control text-end"
                                                                value={produto.valor_proposta}
                                                                onChange={(e) => handleProdutoChange(index, parseFloat(e.target.value) || 0)}
                                                                step="0.01"
                                                                min="0"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-bold align-middle">
                                                        <div className="text-truncate">
                                                            {formatCurrency(produto.quantidade * produto.valor_proposta)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {produtos.length === 0 && (
                                                <tr>
                                                    <td colSpan={4} className="text-center py-3">
                                                        Nenhum produto encontrado.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot className="table-light">
                                            <tr>
                                                <td colSpan={3} className="text-end fw-bold fs-6 align-middle">Valor Total da Renovação:</td>
                                                <td className="text-end fw-bold fs-6 text-primary align-middle">
                                                    {formatCurrency(valorTotalCalculado)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-end mt-4">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={onClose}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Renovando...
                                    </>
                                ) : (
                                    'Confirmar Renovação'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default RenovacaoContratoModal;
